/**
 * ?f= / ?lang= → funnel + content + settings, schema-validated at load.
 *
 * Fallbacks (ARCHITECTURE.md):
 *   - unknown ?f  → settings.defaultFunnel
 *   - missing content for ?lang → settings.defaultLang file
 *   - schema error → visible error page in dev, defaultFunnel in prod
 *
 * ajv is imported lazily so it lands in its own chunk and never weighs on the
 * main bundle budget (< 180 kB gz).
 */
import type { Config, Content, Funnel, Settings } from './types.ts';

/** thrown for a URL that names no funnel — the bare host is not a funnel */
export class NoFunnelSelected extends Error {
  constructor() {
    super('no funnel in the URL');
    this.name = 'NoFunnelSelected';
  }
}

export class ConfigError extends Error {
  constructor(message: string, readonly detail: string[] = []) {
    super(message);
    this.name = 'ConfigError';
  }
}

const json = async <T>(url: string): Promise<T> => {
  const res = await fetch(url, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new ConfigError(`${url} → HTTP ${res.status}`);
  return (await res.json()) as T;
};

let validators: {
  funnel: (d: unknown) => true | string[];
  content: (d: unknown) => true | string[];
  settings: (d: unknown) => true | string[];
} | null = null;

async function getValidators() {
  if (validators) return validators;
  const [{ default: Ajv }, funnelSchema, contentSchema, settingsSchema] = await Promise.all([
    import('ajv/dist/2020.js'),
    json<object>('/schema/funnel.schema.json'),
    json<object>('/schema/content.schema.json'),
    json<object>('/schema/settings.schema.json'),
  ]);
  const ajv = new Ajv({ allErrors: true, strict: false });
  const wrap = (schema: object) => {
    const v = ajv.compile(schema);
    return (data: unknown): true | string[] =>
      v(data) ? true : (v.errors ?? []).map((e) => `${e.instancePath || '/'} ${e.message ?? ''}`);
  };
  validators = { funnel: wrap(funnelSchema), content: wrap(contentSchema), settings: wrap(settingsSchema) };
  return validators;
}

/** paths the SPA owns that are never a funnel code */
const RESERVED = new Set(['kit', 'assets', 'config', 'schema', 'favicon.ico', 'index.html']);
const CODE = /^[a-z0-9][a-z0-9-]*$/;
const LANG = /^[a-z]{2}(-[A-Z]{2})?$/;

/**
 * Funnel code and language from the URL path, so each angle has its own link:
 *
 *   /                     → settings.defaultFunnel
 *   /views-v1             → that funnel
 *   /views-v1/de          → that funnel in German
 *   /?f=views-v1&lang=de  → the same thing; query params still win, for old links
 *
 * Serving this needs the SPA fallback that public/_redirects (Pages) and
 * docker/nginx.conf both already do.
 */
export function routeFromPath(pathname: string = location.pathname): { code?: string; lang?: string } {
  const parts = pathname.split('/').filter(Boolean);
  if (!parts.length || RESERVED.has(parts[0])) return {};
  const [first, second] = parts;
  // /en is a language on the default funnel, not a funnel called "en"
  if (LANG.test(first) && !second) return { lang: first };
  if (!CODE.test(first)) return {};
  return { code: first, lang: second && LANG.test(second) ? second : undefined };
}

/** the canonical link for a funnel, used for the redirect off ?f= */
export const funnelPath = (code: string, lang?: string): string => `/${code}${lang ? `/${lang}` : ''}`;

export function pickLang(requested: string | null, settings: Settings): string {
  const supported = settings.languages ?? [settings.defaultLang];
  if (requested && supported.includes(requested)) return requested;
  const nav = typeof navigator !== 'undefined' ? (navigator.language ?? '').slice(0, 2) : '';
  if (nav && supported.includes(nav)) return nav;
  return settings.defaultLang;
}

async function loadPair(code: string, lang: string, settings: Settings, validate: Awaited<ReturnType<typeof getValidators>>) {
  const funnel = await json<Funnel>(`/config/funnels/${code}.json`);
  const funnelErrors = validate.funnel(funnel);
  if (funnelErrors !== true) throw new ConfigError(`funnel "${code}" fails its schema`, funnelErrors);

  let content: Content;
  let used = lang;
  try {
    content = await json<Content>(`/config/content/${code}.${lang}.json`);
  } catch {
    used = funnel.defaultLang ?? settings.defaultLang;
    content = await json<Content>(`/config/content/${code}.${used}.json`);
  }
  const contentErrors = validate.content(content);
  if (contentErrors !== true) throw new ConfigError(`content "${code}.${used}" fails its schema`, contentErrors);

  return { funnel, content, lang: used };
}

export async function loadConfig(search: string = location.search, pathname: string = location.pathname): Promise<Config> {
  const params = new URLSearchParams(search);
  const settings = await json<Settings>('/config/settings.json');
  const validate = await getValidators();

  const settingsErrors = validate.settings(settings);
  if (settingsErrors !== true) throw new ConfigError('settings.json fails its schema', settingsErrors);

  const path = routeFromPath(pathname);
  // Each funnel is reached by its own link. A bare host names no funnel and
  // must not silently open one — there will be several.
  const requested = params.get('f') || path.code;
  if (!requested) throw new NoFunnelSelected();
  const lang = pickLang(params.get('lang') ?? path.lang ?? null, settings);

  try {
    const pair = await loadPair(requested, lang, settings, validate);
    return { ...pair, settings };
  } catch (err) {
    if (import.meta.env.DEV || requested === settings.defaultFunnel) throw err;
    // production: one funnel being broken must not take the traffic down
    console.error('[config] falling back to defaultFunnel', err);
    const pair = await loadPair(settings.defaultFunnel, lang, settings, validate);
    return { ...pair, settings };
  }
}
